import { z } from 'zod';
import { FabricFacesSchema } from './fabric-faces.schema';
import { PanelFinishSchema } from './panel-finish.schema';
import {
  UpholsteryDesignSchema,
  UpholsterySpringConfigSchema,
  UpholsteryFoamLayerSchema,
} from './upholstery-design.schema';
// Imported from the module directly rather than the utils barrel, so schemas
// and utils/index.ts cannot form an import cycle.
import { DimLinkSchema } from './dim-link.schema';

// Panel dimension links live in dim-link.schema.ts, shared with foam. A link
// makes a dimension track the component's width, depth or height; null (the
// default) means static, so every panel in every saved config parses as static.

// ── Sub-schemas for banquette_config JSON ──

export const BanquetteRailSchema = z.object({
  name: z.string(),
  width: z.number().min(0),
  /** When set, `width` is the base and tracks this component axis. */
  width_link: DimLinkSchema,
  /**
   * The rail's second dimension. null is the legacy encoding for "spans the
   * run" and resolves to the component's run axis — see resolveRailLength.
   * Never user-selectable: the first save writes a concrete value.
   */
  length: z.number().min(0).nullable().default(null),
  /** When set, `length` is the base and tracks this component axis. */
  length_link: DimLinkSchema,
  material_id: z.string().uuid().nullable(),
  /**
   * Run a linear-foot material's boards along this panel's SHORTER dimension
   * rather than its longer one. Only linear-foot materials read it, and only
   * because rips round up to whole boards — see boardRunInches. Defaults to
   * false, so every config saved before this existed keeps the longer side as
   * the run.
   */
  board_run_flipped: z.boolean().default(false),
  finish: PanelFinishSchema,
});

export const BanquetteAdditionalPanelSchema = z.object({
  name: z.string(),
  width: z.number().min(0),
  /** When set, `width` is the base and tracks this component axis. */
  width_link: DimLinkSchema,
  height: z.number().min(0),
  /** When set, `height` is the base and tracks this component axis. */
  height_link: DimLinkSchema,
  qty_per_section: z.number().int().min(1).default(1),
  material_id: z.string().uuid().nullable(),
  /**
   * Run a linear-foot material's boards along this panel's SHORTER dimension
   * rather than its longer one. Only linear-foot materials read it, and only
   * because rips round up to whole boards — see boardRunInches. Defaults to
   * false, so every config saved before this existed keeps the longer side as
   * the run.
   */
  board_run_flipped: z.boolean().default(false),
  finish: PanelFinishSchema,
});

export const BanquetteFramingSchema = z.object({
  side_panel_material_id: z.string().uuid().nullable(),
  rib_material_id: z.string().uuid().nullable(),
  /** Profile used by any finish-enabled panel that does not name its own. */
  default_finish_id: z.string().uuid().nullable().default(null),
  side_panel_finish: PanelFinishSchema,
  rib_finish: PanelFinishSchema,
  rib_mode: z.enum(['auto', 'manual']).default('auto'),
  rib_spacing: z.number().min(0).default(24),
  rib_count: z.number().int().min(0).default(0),
  waste_factor: z.number().min(0).max(1).default(0.10),
  curved_section_count: z.number().int().min(0).default(0),
  curved_section_waste_factor: z.number().min(0).max(1).default(0),
  rails: z.array(BanquetteRailSchema).default([]),
  additional_panels: z.array(BanquetteAdditionalPanelSchema).default([]),
});

export const BanquettePolylineSchema = z.object({
  points: z.array(z.tuple([z.number(), z.number()])),
  length_px: z.number().min(0),
  length_real: z.number().nullable().optional(),
  length_unit: z.string().nullable().optional(),
});

export const PanelRoleEnum = z.enum(['front', 'side', 'back']);
export type PanelRole = z.infer<typeof PanelRoleEnum>;

export const BanquetteSlotSchema = z.object({
  id: z.string(),
  name: z.string(),
  material_id: z.string().uuid().nullable(),
  color: z.string(),
  calc_mode: z.enum(['linear', 'area']).default('linear'),
  panel_role: PanelRoleEnum.default('front'),
  polylines: z.array(BanquettePolylineSchema).default([]),
  finish_id_side1: z.string().uuid().nullable().optional(),
  finish_id_side2: z.string().uuid().nullable().optional(),
  // Fabric-specific (only used when material has fabric_subtype)
  fabric_wrap_value: z.number().nullable().default(null),
  fabric_staple_allowance: z.number().default(1),
});

export const BanquetteConfigSchema = z.object({
  product_type: z.literal('banquette_base'),
  code: z.string(),
  name: z.string(),
  lf_quantity: z.number().min(0),
  manual_sections: z.boolean().default(false),
  section_count: z.number().int().min(1).default(1),
  profile_depth: z.number().min(0).default(18),
  profile_height: z.number().min(0).default(20),
  /** Reference depth the dynamic panels were authored against. Null = never
   *  baselined, which makes every delta zero. Must never auto-follow the live
   *  dimension — see the spec's freezing rule. */
  base_depth: z.number().min(0).nullable().default(null),
  /** Reference height the dynamic panels were authored against. */
  base_height: z.number().min(0).nullable().default(null),
  /** Reference width the dynamic foam layers were authored against. Null =
   *  never baselined, which makes every delta zero. Must never auto-follow the
   *  live dimension — see the spec's freezing rule. */
  base_width: z.number().min(0).nullable().default(null),

  // ── Upholstery ──
  // A base may be clad (fabric straight over the panel) or padded. Every field
  // here defaults to inert, so a config saved before upholstery existed costs
  // exactly what it did before. No substrate_mode: a base always has framing,
  // unlike a back cushion, which can be a simple backer panel.
  design: UpholsteryDesignSchema.default({}),
  foam_layers: z.array(UpholsteryFoamLayerSchema).default([]),
  foam_waste_factor: z.number().min(0).max(1).default(0.10),
  /** Extra foam waste on curved sections only, blended by the curved share. */
  foam_curved_waste_factor: z.number().min(0).max(1).default(0),
  dacron_material_id: z.string().uuid().nullable().default(null),
  dacron_waste_factor: z.number().min(0).max(1).default(0.10),
  /** Extra dacron waste on curved sections only. */
  dacron_curved_waste_factor: z.number().min(0).max(1).default(0),
  springs: UpholsterySpringConfigSchema.default({}),

  // Per-line-item fabric allowances
  fabric_design_pct: z.number().min(0).nullable().optional(),
  fabric_pattern_allowance_pct: z.number().min(0).default(0),
  /** Which box faces receive fabric. Absent means all six — see resolveFaces. */
  fabric_faces: FabricFacesSchema.optional(),
  /** Whether the Oversized Rules factor applies. Absent means on — only an
   *  explicit false opts out (see resolveOversize). Deliberately not
   *  defaulted: the Cria price API reads configs as raw JSON with no schema
   *  defaults, so "absent is on" has to hold at the reader regardless. */
  oversize_enabled: z.boolean().optional(),

  viewport_id: z.string().uuid().nullable().optional(),
  image_scale_ppi: z.number().min(0).default(0),
  slots: z.array(BanquetteSlotSchema).default([]),
  framing: BanquetteFramingSchema,
}).superRefine((cfg, ctx) => {
  const r = cfg.framing?.curved_section_count ?? 0;
  if (r > cfg.section_count) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Curved sections cannot exceed total sections',
      path: ['framing', 'curved_section_count'],
    });
  }
});

// ── Template CRUD schemas ──

export const BanquetteBaseTemplateSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  banquette_config: z.string(),
  active: z.boolean(),
  created_by: z.string().uuid().nullable(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export const CreateBanquetteBaseTemplateSchema = z.object({
  code: z.string().min(1, 'Code is required'),
  name: z.string().min(1, 'Name is required'),
  description: z.string().nullable().optional(),
  banquette_config: z.string().default('{}'),
  active: z.boolean().default(true),
});

export const UpdateBanquetteBaseTemplateSchema = CreateBanquetteBaseTemplateSchema.partial();

// ── Type exports ──

export type BanquetteRail = z.infer<typeof BanquetteRailSchema>;
export type BanquetteAdditionalPanel = z.infer<typeof BanquetteAdditionalPanelSchema>;
export type BanquetteFraming = z.infer<typeof BanquetteFramingSchema>;
export type BanquettePolyline = z.infer<typeof BanquettePolylineSchema>;
export type BanquetteSlot = z.infer<typeof BanquetteSlotSchema>;
export type BanquetteConfig = z.infer<typeof BanquetteConfigSchema>;
export type BanquetteBaseTemplate = z.infer<typeof BanquetteBaseTemplateSchema>;
export type CreateBanquetteBaseTemplate = z.infer<typeof CreateBanquetteBaseTemplateSchema>;
export type UpdateBanquetteBaseTemplate = z.infer<typeof UpdateBanquetteBaseTemplateSchema>;
